import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DndContext, DragEndEvent, PointerSensor, TouchSensor,
  closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, MealType, RecipeListItem, ScaledRecipe, isSubrecipe, PrepAheadKind, PREP_AHEAD_KINDS } from "../api/client";
import { INGREDIENT_UNITS, MEAL_TYPE_LABELS } from "../constants";
import { PhotoPicker } from "../components/PhotoPicker";
import { Thumb } from "../components/Thumb";
import { IngredientKindIcon } from "../components/IngredientKindIcon";

interface IngredientRow {
  uid: string;
  name: string;
  quantity: number;
  unit: string;
  note?: string;
  prepAhead?: PrepAheadKind | null;
  prepLeadDays?: number;
  prepNote?: string | null;
}

let _uid = 0;
function newUid() { return String(++_uid); }

interface FormData {
  name: string;
  meal_type: MealType;
  base_servings: number;
  prep_time_min?: number;
  notes?: string;
  imageUrl?: string | null;
  subrecipe_ids: number[];
  tags: string[];
  ingredients: IngredientRow[];
  steps: string[];
}

const EMPTY: FormData = {
  name: "",
  meal_type: "lunch",
  base_servings: 4,
  imageUrl: null,
  subrecipe_ids: [],
  tags: [],
  ingredients: [{ uid: newUid(), name: "", quantity: 1, unit: "u" }],
  steps: [""],
};

const MEAL_TYPES: { value: MealType; label: string }[] = [
  { value: "breakfast", label: MEAL_TYPE_LABELS.breakfast },
  { value: "lunch", label: MEAL_TYPE_LABELS.lunch },
  { value: "snack", label: MEAL_TYPE_LABELS.snack },
  { value: "subrecipe", label: MEAL_TYPE_LABELS.subrecipe },
];

interface ZodIssue {
  path: (string | number)[];
  message: string;
  code?: string;
  options?: string[];
  received?: string;
}

function formatImportError(status: number, data: unknown): string {
  if (status === 401) return "No autenticat. Torna a iniciar sessió.";
  if (!data || typeof data !== "object") {
    return `Error ${status} importing recepta.`;
  }
  const d = data as { error?: string; issues?: ZodIssue[] };
  if (d.issues && d.issues.length > 0) {
    const lines = d.issues.slice(0, 5).map((iss) => {
      const path = iss.path.length > 0 ? iss.path.join(".") : "(arrel)";
      if (iss.code === "invalid_enum_value" && iss.options) {
        return `Camp "${path}": valor "${iss.received}" no permès. Opcions: ${iss.options.join(", ")}.`;
      }
      return `Camp "${path}": ${iss.message}`;
    });
    if (d.issues.length > 5) lines.push(`...i ${d.issues.length - 5} més.`);
    return lines.join("\n");
  }
  return d.error ?? `Error ${status}.`;
}

export function RecipeEdit() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const duplicateFromId = searchParams.get("duplica");
  const nav = useNavigate();
  const [data, setData] = useState<FormData>(EMPTY);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadFromId = id ?? duplicateFromId;
  const isDuplicate = !id && !!duplicateFromId;
  const [availableSubrecipes, setAvailableSubrecipes] = useState<RecipeListItem[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    api.get<RecipeListItem[]>("/recipes?subrecipe=true").then((list) => {
      setAvailableSubrecipes(id ? list.filter((r) => r.id !== Number(id)) : list);
    });
  }, [id]);

  useEffect(() => {
    api.get<string[]>("/recipes/tags").then(setAvailableTags);
  }, []);

  function addTag(raw: string) {
    const t = raw.trim();
    if (!t) return;
    setData((d) =>
      d.tags.some((x) => x.toLowerCase() === t.toLowerCase()) ? d : { ...d, tags: [...d.tags, t] }
    );
    setTagInput("");
  }

  function removeTag(tag: string) {
    setData((d) => ({ ...d, tags: d.tags.filter((t) => t !== tag) }));
  }

  useEffect(() => {
    if (!loadFromId) return;
    api.get<ScaledRecipe>(`/recipes/${loadFromId}?servings=0`).then((r) => {
      setData({
        name: isDuplicate ? `${r.name} (còpia)` : r.name,
        meal_type: r.mealType,
        base_servings: r.baseServings,
        prep_time_min: r.prepTimeMin ?? undefined,
        notes: r.notes ?? undefined,
        imageUrl: r.imageUrl ?? null,
        subrecipe_ids: r.subrecipes.map((s) => s.id),
        tags: r.tags,
        ingredients:
          r.ingredients.length > 0
            ? r.ingredients.map((i) => ({
                uid: newUid(),
                name: i.name,
                quantity: i.quantity / r.factor,
                unit: i.unit,
                note: i.note ?? undefined,
                prepAhead: i.prepAhead ?? null,
                prepLeadDays: i.prepLeadDays ?? 1,
                prepNote: i.prepNote ?? null,
              }))
            : [{ uid: newUid(), name: "", quantity: 1, unit: "u" }],
        steps: r.steps.length > 0 ? r.steps : [""],
      });
    });
  }, [loadFromId, isDuplicate]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...data,
        ingredients: data.ingredients.filter((i) => i.name.trim()),
        steps: data.steps.filter((s) => s.trim()),
      };
      if (id) {
        await api.patch(`/recipes/${id}`, payload);
        nav(`/receptes/${id}`);
      } else {
        await api.post(`/recipes`, payload);
        nav("/receptes");
      }
    } finally {
      setBusy(false);
    }
  }

  async function importJson() {
    setJsonError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e: any) {
      setJsonError(`JSON mal format: ${e.message ?? "no es pot llegir"}`);
      return;
    }
    try {
      const res = await fetch("/api/recipes/import-json", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed),
      });
      if (res.ok) {
        nav("/receptes");
        return;
      }
      const resData = await res.json().catch(() => null);
      setJsonError(formatImportError(res.status, resData));
    } catch (e: any) {
      setJsonError(e.message ?? "Error de connexió");
    }
  }

  function updateIngredient(idx: number, patch: Partial<IngredientRow>) {
    setData((d) => ({
      ...d,
      ingredients: d.ingredients.map((i, k) => (k === idx ? { ...i, ...patch } : i)),
    }));
  }

  function onIngredientDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setData((d) => {
      const from = d.ingredients.findIndex((i) => i.uid === active.id);
      const to = d.ingredients.findIndex((i) => i.uid === over.id);
      return { ...d, ingredients: arrayMove(d.ingredients, from, to) };
    });
  }

  const ingSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
  );

  function updateStep(idx: number, text: string) {
    setData((d) => ({ ...d, steps: d.steps.map((s, k) => (k === idx ? text : s)) }));
  }

  function cancel() {
    nav(id ? `/receptes/${id}` : "/receptes");
  }

  const tagSuggestions = availableTags.filter(
    (t) => !data.tags.some((x) => x.toLowerCase() === t.toLowerCase())
  );

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      {/* Barra enganxosa */}
      <div className="save-bar">
        <h1 style={{ fontSize: 24 }}>
          {id ? "Edita recepta" : isDuplicate ? "Duplica recepta" : "Nova recepta"}
        </h1>
        <div className="flex items-center gap-2">
          <button type="button" className="btn btn-ghost" onClick={cancel}>
            Cancel·la
          </button>
          <button type="submit" form="recipeForm" disabled={busy} className="btn btn-primary">
            {busy ? "Desant…" : "Desa"}
          </button>
        </div>
      </div>

      {/* Importa des de JSON (només nova recepta) */}
      {!id && (
        <section className="card" style={{ overflow: "hidden", marginBottom: 18 }}>
          <button type="button" className="collapsible-toggle" onClick={() => setShowJson((v) => !v)}>
            <span className="flex items-baseline gap-2.5">
              <span style={{ fontFamily: "var(--font-head)", fontWeight: 600, fontSize: 15 }}>
                Importa des de JSON
              </span>
              <span className="text-muted" style={{ fontWeight: 700, fontSize: 12.5 }}>
                enganxa&apos;l
              </span>
            </span>
            <ChevronIcon open={showJson} />
          </button>
          {showJson && (
            <div className="collapsible-body flex flex-col gap-3">
              <p className="text-xs text-muted">
                Format:{" "}
                <code className="text-foreground">
                  {`{ "name", "meal_type", "base_servings", "prep_time_min", "tags": [], "ingredients": [{"name","quantity","unit"}], "steps": [] }`}
                </code>
              </p>
              <textarea
                className="nm-input"
                rows={8}
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder='{"name": "Lentejas", ...}'
              />
              {jsonError && (
                <pre className="whitespace-pre-wrap break-words rounded-xl border border-danger/40 bg-danger-soft p-2 text-xs text-danger">
                  {jsonError}
                </pre>
              )}
              <button type="button" className="btn btn-soft self-start" onClick={importJson}>
                Importa
              </button>
            </div>
          )}
        </section>
      )}

      <form id="recipeForm" onSubmit={submit} className="flex flex-col gap-[18px]">
        {/* Bàsics */}
        <FormSection title="Bàsics">
          <div className="field">
            <label htmlFor="name">Nom de la recepta</label>
            <input
              id="name"
              required
              className="nm-input"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              placeholder="Ex: Fideuà de la iaia"
            />
          </div>

          <div className="form-row2">
            <div className="field">
              <label htmlFor="mealType">Tipus d&apos;àpat</label>
              <select
                id="mealType"
                className="nm-input"
                value={data.meal_type}
                onChange={(e) => {
                  const next = e.target.value as MealType;
                  setData((d) => ({
                    ...d,
                    meal_type: next,
                    subrecipe_ids: next === "subrecipe" ? [] : d.subrecipe_ids,
                  }));
                }}
              >
                {MEAL_TYPES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field" style={{ flex: "0 0 auto" }}>
              <label>Racions base</label>
              <div className="stepper">
                <button
                  type="button"
                  onClick={() => setData((d) => ({ ...d, base_servings: Math.max(1, d.base_servings - 1) }))}
                  aria-label="Menys racions"
                >
                  −
                </button>
                <b>{data.base_servings}</b>
                <button
                  type="button"
                  onClick={() => setData((d) => ({ ...d, base_servings: d.base_servings + 1 }))}
                  aria-label="Més racions"
                >
                  +
                </button>
              </div>
            </div>

            <div className="field" style={{ flex: "0 0 auto" }}>
              <label htmlFor="time">Temps (min)</label>
              <input
                id="time"
                type="number"
                min={0}
                className="nm-input"
                style={{ width: 120 }}
                value={data.prep_time_min ?? ""}
                onChange={(e) =>
                  setData({ ...data, prep_time_min: e.target.value ? parseInt(e.target.value) : undefined })
                }
              />
            </div>
          </div>

          <div className="field">
            <label>Etiquetes</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {data.tags.map((t) => (
                <span
                  key={t}
                  className="tag accent"
                  style={{ display: "inline-flex", gap: 5, alignItems: "center", padding: "5px 8px" }}
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    aria-label={`Treu ${t}`}
                    style={{ border: 0, background: "none", cursor: "pointer", color: "inherit", lineHeight: 0 }}
                  >
                    <CloseIcon />
                  </button>
                </span>
              ))}
              <input
                className="nm-input"
                style={{ width: 200 }}
                list="tag-suggestions"
                placeholder="Afegeix i prem Retorn"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && tagInput.trim()) {
                    e.preventDefault();
                    addTag(tagInput);
                  }
                }}
              />
              <datalist id="tag-suggestions">
                {tagSuggestions.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
          </div>

          {!isSubrecipe(data.meal_type) && availableSubrecipes.length > 0 && (
            <div className="field">
              <label>Subreceptes enllaçades</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {availableSubrecipes.map((sr) => {
                  const on = data.subrecipe_ids.includes(sr.id);
                  return (
                    <button
                      key={sr.id}
                      type="button"
                      className={`chip ${on ? "active" : ""}`}
                      onClick={() =>
                        setData((d) => ({
                          ...d,
                          subrecipe_ids: on
                            ? d.subrecipe_ids.filter((x) => x !== sr.id)
                            : [...d.subrecipe_ids, sr.id],
                        }))
                      }
                    >
                      {on ? "✓ " : ""}
                      {sr.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </FormSection>

        {/* Ingredients */}
        <FormSection title={`Ingredients (per ${data.base_servings} ${data.base_servings === 1 ? "ració" : "racions"})`}>
          <DndContext sensors={ingSensors} collisionDetection={closestCenter} onDragEnd={onIngredientDragEnd}>
            <SortableContext items={data.ingredients.map((i) => i.uid)} strategy={verticalListSortingStrategy}>
              <div className="ing-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {data.ingredients.map((ing, idx) => (
                  <SortableIngredientRow
                    key={ing.uid}
                    ing={ing}
                    idx={idx}
                    onUpdate={(patch) => updateIngredient(idx, patch)}
                    onRemove={() => setData((d) => ({ ...d, ingredients: d.ingredients.filter((_, k) => k !== idx) }))}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button
            type="button"
            className="btn btn-soft self-start"
            style={{ marginTop: 10 }}
            onClick={() =>
              setData((d) => ({ ...d, ingredients: [...d.ingredients, { uid: newUid(), name: "", quantity: 1, unit: "u" }] }))
            }
          >
            + Ingredient
          </button>
        </FormSection>

        {/* Passos */}
        <FormSection title="Passos">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {data.steps.map((s, idx) => (
              <div key={idx} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span className="step-num">{idx + 1}</span>
                <textarea
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = `${el.scrollHeight}px`;
                    }
                  }}
                  className="nm-input"
                  style={{ flex: 1, minHeight: 64, overflow: "hidden" }}
                  value={s}
                  onChange={(e) => {
                    updateStep(idx, e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  placeholder="Descriu el pas…"
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  style={{ marginTop: 7 }}
                  aria-label="Elimina pas"
                  onClick={() => setData((d) => ({ ...d, steps: d.steps.filter((_, k) => k !== idx) }))}
                >
                  <CloseIcon />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-soft self-start"
            style={{ marginTop: 10 }}
            onClick={() => setData((d) => ({ ...d, steps: [...d.steps, ""] }))}
          >
            + Pas
          </button>
        </FormSection>

        {/* Notes */}
        <FormSection title="Notes">
          <textarea
            className="nm-input"
            rows={3}
            style={{ resize: "vertical" }}
            value={data.notes ?? ""}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            placeholder="Trucs, variacions, d&apos;on ve la recepta…"
          />
        </FormSection>

        {/* Foto */}
        <FormSection title="Foto">
          <div className="flex flex-col gap-1">
            <PhotoPicker
              type="recipe"
              imageUrl={data.imageUrl}
              name={data.name}
              meal={data.meal_type}
              size={120}
              onChange={(url) => setData({ ...data, imageUrl: url })}
            />
            <p className="text-muted" style={{ fontSize: 12 }}>
              JPG o PNG · es retalla a 4:3
            </p>
          </div>
        </FormSection>

        {/* Esborra */}
        {id && (
          <button
            type="button"
            className="btn btn-danger self-start"
            onClick={async () => {
              if (!confirm("Vols esborrar aquesta recepta definitivament?")) return;
              try {
                await api.del(`/recipes/${id}`);
                window.dispatchEvent(new Event("cookmenu:data-changed"));
                nav("/receptes");
              } catch {
                alert("Error esborrant la recepta");
              }
            }}
          >
            🗑 Esborra la recepta
          </button>
        )}
      </form>
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card" style={{ padding: "18px 20px" }}>
      <h3 style={{ fontSize: 16, marginBottom: 14 }}>{title}</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </section>
  );
}

interface IngredientCatalogItem {
  id: number;
  name: string;
  defaultUnit: string | null;
  barcode?: string | null;
}

function IngredientNameInput({
  value,
  onChange,
  onUnitSuggested,
}: {
  value: string;
  onChange: (name: string) => void;
  onUnitSuggested: (unit: string) => void;
}) {
  const [suggestions, setSuggestions] = useState<IngredientCatalogItem[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function search(q: string) {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get<IngredientCatalogItem[]>(`/ingredients?q=${encodeURIComponent(q)}`);
        setSuggestions(res.slice(0, 8));
        setOpen(res.length > 0);
        setHighlighted(0);
      } catch {}
    }, 220);
  }

  function select(item: IngredientCatalogItem) {
    onChange(item.name);
    if (item.defaultUnit) onUnitSuggested(item.defaultUnit);
    setSuggestions([]);
    setOpen(false);
  }

  return (
    <div className="ing-name-wrap" style={{ position: "relative", flex: 1, minWidth: 110 }}>
      <input
        className="nm-input"
        style={{ width: "100%" }}
        placeholder="Ingredient"
        value={value}
        autoComplete="off"
        aria-autocomplete="list"
        aria-label="Ingredient"
        onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        onKeyDown={(e) => {
          if (!open || suggestions.length === 0) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, suggestions.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && suggestions[highlighted]) { e.preventDefault(); select(suggestions[highlighted]); }
          else if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && suggestions.length > 0 && (
        <div className="ing-ac-drop" role="listbox">
          {suggestions.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={i === highlighted}
              className={`ing-ac-item${i === highlighted ? " hl" : ""}`}
              onMouseDown={() => select(item)}
              onMouseEnter={() => setHighlighted(i)}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                <IngredientKindIcon packaged={item.barcode != null} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
              </span>
              {item.defaultUnit && <span className="ing-ac-unit">{item.defaultUnit}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SortableIngredientRow({
  ing, idx, onUpdate, onRemove,
}: {
  ing: IngredientRow;
  idx: number;
  onUpdate: (patch: Partial<IngredientRow>) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ing.uid });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };
  const prepActive = ing.prepAhead != null;
  const [prepOpen, setPrepOpen] = useState(prepActive);
  const activeKind = PREP_AHEAD_KINDS.find((k) => k.value === ing.prepAhead);

  // Text lliure per als camps numèrics: es desacobla del valor numèric perquè
  // esborrar el dígit (deixant el camp buit un instant) no el torni a omplir
  // amb "0" abans que l'usuari pugui escriure el nou número.
  const [qtyText, setQtyText] = useState(String(ing.quantity));
  const [leadText, setLeadText] = useState(String(ing.prepLeadDays ?? 1));
  return (
    <div ref={setNodeRef} style={style} className="ing-edit" data-prep={prepActive ? "on" : undefined}>
      <div className="ing-row1">
        <button type="button" className="ing-drag-handle" aria-label="Arrossega per reordenar" {...attributes} {...listeners}>
          <DragHandleIcon />
        </button>
        <input
          className="nm-input ing-qty-input"
          type="number"
          step={0.1}
          style={{ width: 64 }}
          value={qtyText}
          onChange={(e) => {
            const raw = e.target.value;
            setQtyText(raw);
            const parsed = parseFloat(raw);
            if (!Number.isNaN(parsed)) onUpdate({ quantity: parsed });
          }}
          onBlur={() => {
            if (Number.isNaN(parseFloat(qtyText))) setQtyText(String(ing.quantity));
          }}
          aria-label="Quantitat"
        />
        <select
          className="nm-input ing-unit-select"
          style={{ width: 74 }}
          value={ing.unit}
          onChange={(e) => onUpdate({ unit: e.target.value })}
          aria-label="Unitat"
        >
          {INGREDIENT_UNITS.map((u) => (
            <option key={u.value} value={u.value}>{u.value}</option>
          ))}
        </select>
        <IngredientNameInput
          value={ing.name}
          onChange={(name) => onUpdate({ name })}
          onUnitSuggested={(unit) => {
            if (!ing.unit || ing.unit === "u") onUpdate({ unit });
          }}
        />
        <button
          type="button"
          className="ing-remove"
          aria-label="Elimina ingredient"
          onClick={onRemove}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="ing-row2">
        <button
          type="button"
          className={`ing-prep-toggle${prepActive ? " active" : ""}`}
          aria-label="Preparació prèvia"
          aria-pressed={prepOpen}
          title={prepActive ? `${activeKind?.label} · ${ing.prepLeadDays ?? 1} dia/es abans` : "Preparació prèvia (dies abans)"}
          onClick={() => setPrepOpen((v) => !v)}
        >
          {prepActive ? (activeKind?.emoji ?? "⏲️") : "⏲️"}
        </button>
        <input
          className="nm-input ing-note-input"
          placeholder="Nota (opcional)"
          value={ing.note ?? ""}
          onChange={(e) => onUpdate({ note: e.target.value || undefined })}
          aria-label="Nota de l'ingredient"
        />
      </div>

      {prepOpen && (
        <div className="ing-prep-panel">
          <span className="ing-prep-hint">Cal preparar-lo abans?</span>
          <div className="ing-prep-kinds">
            <button
              type="button"
              className={`chip ${!prepActive ? "active" : ""}`}
              onClick={() => onUpdate({ prepAhead: null })}
            >
              Cap
            </button>
            {PREP_AHEAD_KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                className={`chip ${ing.prepAhead === k.value ? "active" : ""}`}
                onClick={() => onUpdate({ prepAhead: k.value, prepLeadDays: ing.prepLeadDays ?? 1 })}
              >
                {k.emoji} {k.label}
              </button>
            ))}
          </div>
          {prepActive && (
            <div className="ing-prep-detail">
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                Dies abans
                <input
                  className="nm-input"
                  type="number"
                  min={1}
                  max={7}
                  style={{ width: 64 }}
                  value={leadText}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setLeadText(raw);
                    const parsed = parseInt(raw, 10);
                    if (!Number.isNaN(parsed)) onUpdate({ prepLeadDays: Math.min(7, Math.max(1, parsed)) });
                  }}
                  onBlur={() => {
                    const parsed = parseInt(leadText, 10);
                    if (Number.isNaN(parsed)) setLeadText(String(ing.prepLeadDays ?? 1));
                    else setLeadText(String(Math.min(7, Math.max(1, parsed))));
                  }}
                />
              </label>
              <input
                className="nm-input"
                style={{ flex: 1, minWidth: 140 }}
                placeholder="Nota (ex: 12h en aigua)"
                value={ing.prepNote ?? ""}
                onChange={(e) => onUpdate({ prepNote: e.target.value || null })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DragHandleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <rect x="4" y="3" width="2" height="2" rx="1" />
      <rect x="4" y="7" width="2" height="2" rx="1" />
      <rect x="4" y="11" width="2" height="2" rx="1" />
      <rect x="10" y="3" width="2" height="2" rx="1" />
      <rect x="10" y="7" width="2" height="2" rx="1" />
      <rect x="10" y="11" width="2" height="2" rx="1" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color: "var(--faint)", transform: open ? "rotate(90deg)" : "none", transition: ".15s", flexShrink: 0 }}
      aria-hidden
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
